/**
 * SchadenverlaufCard — presentational Schadenverlauf entry card (P2 Placement Slice 6, Ch 7.2).
 *
 * Shared by BOTH surfaces (the Police «Schadenverlauf» panel and the Ausschreibungs-Screen Tab 1
 * read-only ref). Pure: it renders one SchadenverlaufEintrag as a definition list + a status chip,
 * flags «Demo-Daten» for a fixture source, and (when `has_rohdaten`) exposes a «Rohdaten anzeigen»
 * button via `onShowRohdaten`. An E17 `leer` entry shows the amber warning banner instead of amounts.
 *
 * [Source: apps/web/src/features/entity-detail/components/police-risiko/SollGapSection.tsx] (section/card idiom)
 */

import type { ReactNode } from 'react';
import {
  SCHADENVERLAUF_LEER_HINWEIS,
  SCHADENVERLAUF_STATUS,
  SCHADENVERLAUF_STATUS_LABELS,
  SCHADENVERLAUF_TRIGGER_LABELS,
  isSchadenverlaufDemo,
} from '@brokly/shared';
import type { SchadenverlaufEintrag } from '@brokly/shared';
import { chf, pct, dateShortDe } from '../../../../lib/format';
import { cn } from '../../../../lib/cn';
import { Button } from '../../../../components/ui/Button';

/** CHF / percent with a '-' fallback for a null amount (quote may be missing — Q46). */
function fmtChf(v: number | null): string {
  return v === null ? '-' : chf(v);
}
function fmtPct(v: number | null): string {
  return v === null ? '-' : pct(v);
}

function Row({ label, value, title }: { label: string; value: ReactNode; title?: string }) {
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="text-xs font-medium text-muted-foreground">{label}</dt>
      <dd className="text-sm text-foreground" title={title}>
        {value}
      </dd>
    </div>
  );
}

export function SchadenverlaufCard({
  eintrag,
  actions,
  onShowRohdaten,
}: {
  eintrag: SchadenverlaufEintrag | null;
  actions?: ReactNode;
  onShowRohdaten?: (entryId: string) => void;
}) {
  if (!eintrag) return null;
  const isLeer = eintrag.ergebnis === 'leer';
  const isAktuell = eintrag.status === SCHADENVERLAUF_STATUS.AKTUELL;

  return (
    <div className="rounded-lg border bg-card p-4" data-testid="schadenverlauf-card">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span
            className={cn(
              'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
              isAktuell ? 'bg-status-success/15 text-status-success' : 'bg-muted text-muted-foreground',
            )}
            data-testid="schadenverlauf-status-chip"
          >
            {SCHADENVERLAUF_STATUS_LABELS[eintrag.status]}
          </span>
          {isSchadenverlaufDemo(eintrag.source) && (
            <span
              className="inline-flex items-center rounded-full bg-status-warning/15 px-2 py-0.5 text-xs font-medium text-status-warning"
              data-testid="schadenverlauf-demo-chip"
            >
              Demo-Daten
            </span>
          )}
        </div>
        {actions}
      </div>

      <dl className="grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-3">
        <Row label="Quelle" value={eintrag.source_label} />
        <Row label="Bezugsdatum" value={dateShortDe(eintrag.retrieved_at)} />
        <Row
          label="Periode"
          value={`${dateShortDe(eintrag.periode_von)} - ${dateShortDe(eintrag.periode_bis)}`}
        />
        <Row
          label="Auslöser"
          value={
            <>
              {SCHADENVERLAUF_TRIGGER_LABELS[eintrag.retrieval_trigger]}
              {eintrag.placement_number && (
                <span className="text-muted-foreground"> · {eintrag.placement_number}</span>
              )}
            </>
          }
        />
      </dl>

      {isLeer ? (
        <p
          role="alert"
          className="mt-3 rounded-md border border-status-warning/40 bg-status-warning/10 p-3 text-sm text-status-warning"
          data-testid="schadenverlauf-leer"
        >
          {SCHADENVERLAUF_LEER_HINWEIS}
        </p>
      ) : (
        <dl className="mt-3 grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-3" data-testid="schadenverlauf-amounts">
          <Row label="Schadenanzahl (5 Jahre)" value={eintrag.schadenanzahl_5j ?? '-'} />
          <Row label="Schadensumme (5 Jahre)" value={fmtChf(eintrag.schadensumme_5j)} />
          <Row
            label="Schadenquote / Rendement"
            value={fmtPct(eintrag.schadenquote_rendement)}
            title={eintrag.schadenquote_rendement === null ? 'nicht geliefert' : undefined}
          />
        </dl>
      )}

      {eintrag.has_rohdaten && onShowRohdaten && (
        <div className="mt-3">
          <Button
            size="sm"
            variant="outline"
            onClick={() => onShowRohdaten(eintrag.id)}
            data-testid="schadenverlauf-rohdaten-btn"
          >
            Rohdaten anzeigen
          </Button>
        </div>
      )}
    </div>
  );
}
